import { randomString } from '../random';

export interface ReturnOnlyParams {
    fields?: any;
    return_only?: any;
}

/**
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @class: ReturnOnly
 * @scope:
 * @date: 14.10.2018
 * @time: 22:19
 */
export class ReturnOnly {
    private fields: string | Record<string, any>;

    constructor(fields: string | Record<string, any> = '') {
        this.fields = fields;
    }

    /**
     * JSON.stringify of Return Only feature
     * @param fields
     * @return {*}
     */
    static stringify(fields: string | Record<string, any> = {}) {
        return new ReturnOnly(fields).toString();
    }

    /**
     * JSON.parse of Return Only feature
     * @param fields
     * @returns {Object}
     */
    static parse(fields = '') {
        return new ReturnOnly(fields).parse();
    }

    /**
     * "{ id:true, roles: { id: true } }" -> "id,roles{id}"
     * "[ id, roles, [id]]" -> "id,roles{id}"
     * @return {ReturnOnly.fields}
     */
    toString(fields$?: Record<string, any>): string {
        let { fields } = this;

        fields = fields$ || fields;
        fields = typeof fields === 'object' ? fields : {};

        if (Array.isArray(fields)) {
            fields = this.arrayToObject(fields);
        }

        const keys = Object.keys(fields);

        fields = keys.reduce(
            ({ tree, count }, property) => {
                let value = fields[property];

                if (typeof value === 'object') {
                    if (Array.isArray(value)) {
                        // Get recursive fields
                        value = this.toString(value);
                        if (value) {
                            value = `${property}{${value}}`;
                        }
                    } else {
                        value = this.toString(value);
                        if (value) {
                            value = `${property}{${value}}`;
                        }
                    }
                } else if (typeof value === 'string') {
                    value = `${property}{${value}}`;
                } else {
                    value = `${property}`;
                }

                // Append comma if the property isn't last item
                value = `${value}${count < keys.length - 1 ? ',' : ''}`;

                return {
                    tree: `${tree + value}`,
                    count: count + 1,
                };
            },
            {
                tree: '',
                count: 0,
            },
        ).tree;

        return fields;
    }

    /**
     * "id,roles{id}" -> "{ id:true, roles: { id: true } }"
     * @param fields$
     * @return {ReturnOnly.fields}
     */
    parse(fields$?: string): Record<string, any> {
        let { fields } = this;
        const tokens = {};

        fields = fields$ || fields;
        fields = typeof fields === 'string' ? fields : '';
        fields = fields.replace(/\n/g, '');
        fields = fields.replace(/\s/g, '');
        fields = fields.replace(/^{/, '');
        fields = fields.replace(/}$/, '');
        fields = fields.replace(/{(.*?)}/g, (str, contain) => {
            const token = randomString(5);
            tokens[token] = contain;
            return `(${token})`;
        });

        fields = fields.split(',');

        fields = fields.reduce((tree, key) => {
            key = typeof key === 'string' ? key.trim() : '';

            // noinspection JSUnusedLocalSymbols
            const [, prop, token] = key.match(/^(.*)\((.*)\)/) || [key, key];

            tree[prop] = !token ? true : this.parse(tokens[token]);

            return tree;
        }, {});

        return fields as any;
    }

    arrayToObject(fields: any[] = []) {
        return fields.reduce((obj, field) => {
            if (typeof field === 'string') {
                obj[field] = true;
            } else if (typeof field === 'object') {
                if (Array.isArray(field)) {
                    obj[field[0]] = this.arrayToObject(Array.isArray(field[1]) ? field[1] : field.slice(1));
                }
            }

            return obj;
        }, {});
    }

    static asArray(fields: { [key: string]: any }): any[] {
        return Object.entries(fields).reduce((acc, [key, value]: any) => {
            if (typeof value === 'boolean') {
                return [...acc, key];
            }

            return [...acc, [key, ReturnOnly.asArray(value)]];
        }, [] as any[]);
    }
}
